import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useApp } from "@/context/AuthContext";
import MascotLoader from "@/components/MascotLoader";
import TutorialPlayer from "@/pages/lessons/TutorialPlayer";
import { Confetti } from "@/components/Confetti";
import { ArrowLeft, Trophy, Star, Lock, BookOpen, PenLine, Volume2, VolumeX, WifiOff, CheckCircle2, Loader2, Gauge } from "lucide-react";
import { toast } from "sonner";
import { useAutoplay } from "@/lib/useAutoplay";
import { usePlaybackSpeed } from "@/lib/usePlaybackSpeed";
import { cancelSpeech, setDefaultVoice } from "@/lib/speak";
import { useLessonPreload, useOnlineStatus } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";

import PhonicsEngine from "@/components/engines/PhonicsEngine";
import McqEngine from "@/components/engines/McqEngine";
import CountEngine from "@/components/engines/CountEngine";
import SinkFloatEngine from "@/components/engines/SinkFloatEngine";
import MatchEngine from "@/components/engines/MatchEngine";
import TraceEngine from "@/components/engines/TraceEngine";

const ENGINES = {
  phonics: PhonicsEngine,
  mcq: McqEngine,
  count: CountEngine,
  sink_float: SinkFloatEngine,
  match: MatchEngine,
  trace: TraceEngine,
};

export default function UniversalLesson() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { refreshCurriculum } = useApp();
  const { role } = useAuth();
  const isStudent = role === "student";
  const [autoplay, toggleAutoplay] = useAutoplay();
  const [speed, setSpeed] = usePlaybackSpeed();
  const [lesson, setLesson] = useState(null);
  const [step, setStep] = useState(0);
  const [earned, setEarned] = useState(0);
  const [done, setDone] = useState(false);
  const [phase, setPhase] = useState("learn");

  useEffect(() => {
    api.getLesson(lessonId).then(setLesson).catch(() => setLesson(false));
  }, [lessonId]);

  useEffect(() => () => cancelSpeech(), []);
  useEffect(() => { cancelSpeech(); }, [step, phase]);

  // Language-aware TTS: Foreign Language lessons flip the default voice
  // (e.g. "shimmer" for Spanish, "sage" for French) so the child hears real
  // pronunciation instead of an English narrator reading Spanish words.
  const lessonVoice = lesson && lesson.voice;
  useEffect(() => {
    if (lessonVoice) setDefaultVoice(lessonVoice);
    return () => setDefaultVoice("nova");
  }, [lessonVoice]);

  // Pre-cache TTS + lesson JSON so the student can keep drilling if WiFi drops.
  const preload = useLessonPreload(lesson || null, lessonVoice || "nova");
  const online = useOnlineStatus();

  if (lesson === false) return <div className="p-16 text-center font-heading text-xl text-[#6B7280]">Lesson not found.</div>;
  if (!lesson) return <MascotLoader message="Getting your lesson ready…" />;

  if (lesson.locked) {
    const color = lesson.subject?.color || "#0D9488";
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-4 text-center" data-testid="lesson-locked">
        <div className="flex h-20 w-20 items-center justify-center rounded-full text-white shadow-lg" style={{ background: color }}><Lock size={38} /></div>
        {isStudent ? (
          <>
            <h1 className="mt-6 font-heading text-3xl font-extrabold text-[#1F2937]">This lesson is locked ✨</h1>
            <p className="mt-2 font-body text-lg text-[#6B7280]">Ask a grown-up (a parent or teacher) to unlock “{lesson.title}” for you.</p>
            <div className="mt-8 flex gap-3">
              <button onClick={() => navigate(`/subject/${lesson.subject.id}`)} data-testid="locked-student-back" className="rounded-full bg-[#0D9488] px-6 py-3 font-heading font-bold text-white hover:bg-[#0B7F74]">Back to subject</button>
            </div>
          </>
        ) : (
          <>
            <h1 className="mt-6 font-heading text-3xl font-extrabold text-[#1F2937]">This lesson is a Premium lesson</h1>
            <p className="mt-2 font-body text-lg text-[#6B7280]">Upgrade to open “{lesson.title}” and the full PreK–8 curriculum.</p>
            <div className="mt-8 flex gap-3">
              <button onClick={() => navigate("/pricing")} data-testid="locked-upgrade" className="rounded-full bg-[#0D9488] px-6 py-3 font-heading font-bold text-white hover:bg-[#0B7F74]">See plans</button>
              <button onClick={() => navigate(`/subject/${lesson.subject.id}`)} className="rounded-full border border-[#E5DECF] bg-white px-6 py-3 font-heading font-bold text-[#1F2937] hover:bg-[#F2ECE1]">Back</button>
            </div>
          </>
        )}
      </div>
    );
  }

  const activities = lesson.activities || [];
  const color = lesson.subject?.color || "#0D9488";
  const hasTutorial = (lesson.tutorial?.steps || []).length > 0;

  if (hasTutorial && phase === "learn" && !done) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="mb-3 flex items-center justify-between">
          <button onClick={() => navigate(`/subject/${lesson.subject.id}`)} data-testid="lesson-back" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-[#6B7280] hover:bg-[#F2ECE1]">
            <ArrowLeft size={16} /> {lesson.title}
          </button>
          <div className="flex items-center gap-2">
            <OfflineBadge preload={preload} online={online} />
            <SpeedPicker value={speed} onChange={setSpeed} color={color} />
            <AutoplayToggle on={autoplay} onToggle={toggleAutoplay} color={color} />
          </div>
        </div>
        <PhaseTabs phase={phase} setPhase={setPhase} color={color} />
        <TutorialPlayer tutorial={lesson.tutorial} color={color} subjectName={lesson.subject?.name} autoplay={autoplay} onDone={() => setPhase("practice")} />
      </div>
    );
  }

  const activity = activities[step];

  const handleComplete = async (score) => {
    try {
      const res = await api.subjectAttempt({ activity_id: activity.id, score });
      setEarned((e) => e + (res.points_earned || 0));
      if (res.sticker_earned) {
        toast.success(`New sticker: ${res.sticker_earned.name}! 🌟`, { duration: 5000 });
      }
    } catch (_) {}
    if (step + 1 >= activities.length) {
      setDone(true);
      refreshCurriculum && refreshCurriculum();
    } else {
      setStep(step + 1);
    }
  };

  if (done) {
    return (
      <div className="relative mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-4 text-center">
        <Confetti />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
          className="flex h-24 w-24 items-center justify-center rounded-full text-white shadow-lg" style={{ background: color }}>
          <Trophy size={48} />
        </motion.div>
        <h1 className="mt-6 font-heading text-3xl font-extrabold text-[#1F2937]">Lesson Complete!</h1>
        <p className="mt-2 font-body text-lg text-[#6B7280]">You finished {lesson.title}.</p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#F59E0B]/15 px-5 py-2 font-heading font-bold text-[#B45309]" data-testid="lesson-points">
          <Star size={18} /> +{earned} points
        </div>
        <div className="mt-8 flex gap-3">
          <button onClick={() => navigate(`/subject/${lesson.subject.id}`)} data-testid="lesson-done-subject"
            className="rounded-full bg-[#0D9488] px-6 py-3 font-heading font-bold text-white hover:bg-[#0B7F74]">Back to {lesson.subject.name}</button>
          <button onClick={() => navigate("/")} data-testid="lesson-done-home"
            className="rounded-full border border-[#E5DECF] bg-white px-6 py-3 font-heading font-bold text-[#1F2937] hover:bg-[#F2ECE1]">All Subjects</button>
        </div>
      </div>
    );
  }

  const Engine = ENGINES[activity.engine];

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => navigate(`/subject/${lesson.subject.id}`)} data-testid="lesson-back" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-[#6B7280] hover:bg-[#F2ECE1]">
          <ArrowLeft size={16} /> {lesson.title}
        </button>
        <div className="flex items-center gap-2">
          <SpeedPicker value={speed} onChange={setSpeed} color={color} />
          <AutoplayToggle on={autoplay} onToggle={toggleAutoplay} color={color} />
        </div>
      </div>

      {hasTutorial && <PhaseTabs phase={phase} setPhase={setPhase} color={color} />}

      {/* Step progress */}
      <div className="mb-6 flex items-center gap-2">
        {activities.map((a, i) => (
          <div key={a.id} className="h-2 flex-1 overflow-hidden rounded-full bg-[#F2ECE1]">
            <div className="h-full rounded-full transition-all" style={{ width: i <= step ? "100%" : "0%", background: color }} />
          </div>
        ))}
        <OfflineBadge preload={preload} online={online} />
      </div>

      <div className="rounded-3xl border border-[#E5DECF] bg-white p-6 sm:p-8" data-testid="lesson-activity">
        <div className="mb-4 text-center">
          <div className="font-body text-xs font-bold uppercase tracking-wide text-[#94A3B8]">Step {step + 1} of {activities.length}</div>
          <h2 className="mt-1 font-heading text-2xl font-bold text-[#1F2937]">{activity.title}</h2>
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={activity.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, pointerEvents: "none", transition: { duration: 0.12 } }}>
            {Engine ? (
              <Engine key={activity.id} activity={activity} autoplay={autoplay} onComplete={handleComplete} />
            ) : (
              <div className="text-center">
                <p className="text-[#6B7280]">This activity type isn’t available yet.</p>
                <button onClick={() => handleComplete(100)} className="mt-4 rounded-full bg-[#0D9488] px-6 py-2 font-bold text-white">Skip</button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function AutoplayToggle({ on, onToggle, color }) {
  return (
    <button onClick={onToggle} data-testid="autoplay-toggle" aria-pressed={on}
      title={on ? "Read to me is on — tap to turn off" : "Turn on Read to me (auto-narration)"}
      className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-sm font-bold transition-all ${on ? "text-white border-transparent" : "border-[#E5DECF] bg-white text-[#6B7280] hover:bg-[#F2ECE1]"}`}
      style={on ? { background: color } : {}}>
      {on ? <Volume2 size={16} /> : <VolumeX size={16} />} Read to me
    </button>
  );
}

const SPEED_OPTIONS = [
  { value: 0.75, label: "0.75×", hint: "Slower — great for new readers" },
  { value: 1,    label: "1×",    hint: "Normal speed" },
  { value: 1.25, label: "1.25×", hint: "A little faster" },
  { value: 1.5,  label: "1.5×",  hint: "Speed run" },
];

function SpeedPicker({ value, onChange, color }) {
  const active = SPEED_OPTIONS.find((o) => o.value === value) || SPEED_OPTIONS[1];
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" data-testid="speed-picker">
      <button onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}
        title={`Playback speed: ${active.label}`}
        data-testid="speed-picker-button"
        className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#E5DECF] bg-white px-3 py-1.5 text-sm font-bold text-[#6B7280] hover:bg-[#F2ECE1]">
        <Gauge size={16} /> {active.label}
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-40 cursor-default" aria-label="Close speed menu" onClick={() => setOpen(false)} />
          <div role="listbox" aria-label="Playback speed" className="absolute right-0 z-50 mt-1 w-52 overflow-hidden rounded-2xl border border-[#E5DECF] bg-white shadow-lg" data-testid="speed-picker-menu">
            {SPEED_OPTIONS.map((o) => {
              const on = o.value === value;
              return (
                <button key={o.value} role="option" aria-selected={on}
                  data-testid={`speed-option-${String(o.value).replace(".", "-")}`}
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-[#F2ECE1] ${on ? "font-bold" : ""}`}
                  style={on ? { color } : {}}>
                  <span>{o.label}</span>
                  <span className="text-xs font-normal text-[#94A3B8]">{o.hint}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}


function PhaseTabs({ phase, setPhase, color }) {
  const Tab = ({ id, label, Icon }) => (
    <button onClick={() => setPhase(id)} data-testid={`phase-${id}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${phase === id ? "text-white" : "text-[#6B7280] hover:bg-[#F2ECE1]"}`}
      style={phase === id ? { background: color } : {}}>
      <Icon size={14} /> {label}
    </button>
  );
  return (
    <div className="mb-5 inline-flex gap-1 rounded-full border border-[#E5DECF] bg-white p-1">
      <Tab id="learn" label="Learn" Icon={BookOpen} />
      <Tab id="practice" label="Practice" Icon={PenLine} />
    </div>
  );
}


function OfflineBadge({ preload, online }) {
  // Never shown when there's nothing to preload (e.g., cursive tracing).
  if (!preload || (!preload.total && online)) return null;
  if (!online) {
    return (
      <span data-testid="offline-badge-offline" title="You're offline — cached audio and lesson data will keep working."
        className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#E05A47]/10 px-2 py-0.5 text-[10px] font-bold text-[#B4402F]">
        <WifiOff size={11} /> Offline
      </span>
    );
  }
  if (preload.running) {
    const pct = preload.total ? Math.round((preload.done / preload.total) * 100) : 0;
    return (
      <span data-testid="offline-badge-caching" title={`Pre-caching ${preload.total} phrases so the lesson works offline.`}
        className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#F2ECE1] px-2 py-0.5 text-[10px] font-bold text-[#6B7280]">
        <Loader2 size={11} className="animate-spin" /> Caching {pct}%
      </span>
    );
  }
  if (preload.ready) {
    return (
      <span data-testid="offline-badge-ready" title="This lesson is saved for offline use — WiFi hiccups won't stop the drill."
        className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#0D9488]/10 px-2 py-0.5 text-[10px] font-bold text-[#0B7F74]">
        <CheckCircle2 size={11} /> Offline ready
      </span>
    );
  }
  return null;
}
